"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberList } from "@/lib/parse-number";
import {
  formatPercent,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type VariationReport,
  variationReport,
} from "@/lib/tool-kit/stats-descriptive/variation";

/** English strings. A translated content file overrides these through `ui`. */
export const COEFFICIENT_OF_VARIATION_CALCULATOR_STRINGS = {
  datasetsLabel: "Datasets",
  datasetsOne: "One dataset",
  datasetsTwo: "Compare two datasets",
  sdLabel: "Standard deviation",
  sdSample: "Sample (n − 1)",
  sdPopulation: "Population (n)",
  dataA: "Dataset A — numbers separated by commas, spaces, or new lines",
  dataB: "Dataset B — numbers separated by commas, spaces, or new lines",
  placeholderA: "72, 78, 81, 85, 88, 91, 95",
  placeholderB: "250, 265, 280, 300, 320, 360, 410",
  nameA: "A",
  nameB: "B",
  valuesDetected: "one: value detected|other: values detected",
  tryExample: "Try an example",
  exampleA: "72, 78, 81, 85, 88, 91, 95",
  exampleB: "250, 265, 280, 300, 320, 360, 410",
  clear: "Clear",
  enterPrompt: "Enter at least two numbers in each dataset.",
  results: "Results",
  cv: "Coefficient of variation",
  cvOf: "CV of {name}",
  mean: "Mean",
  sd: "Standard deviation",
  count: "Values (n)",
  colDataset: "Dataset",
  colN: "n",
  colMean: "Mean",
  colSd: "SD",
  colCv: "CV",
  workingTitle: "How it was calculated",
  cvLine: "CV({name}) = s ÷ x̄ × 100% = {sd} ÷ {mean} × 100% = {cv}",
  compareTitle: "Which varies more?",
  compareLine:
    "{higher} varies more relative to its mean: a CV of {high} against {low}, about {ratio} times as much.",
  equalLine: "The two datasets vary equally relative to their means.",
  compareNote:
    "The CV compares spread across different units and scales — which a raw SD cannot do — as long as both datasets are on a ratio scale.",
  zeroMean:
    "The mean of {name} is zero, so its coefficient of variation is undefined.",
  negativeWarning:
    "{name} contains negative values. The CV assumes a ratio scale — positive measurements with a true zero — and means little for data such as temperatures in °C or scores that can fall below zero.",
  highWarning:
    "The CV of {name} is above 100%: the mean is small compared with the spread. Check that the data are on a ratio scale and not centred near zero, where the CV becomes unstable.",
} as const;

type Strings = typeof COEFFICIENT_OF_VARIATION_CALCULATOR_STRINGS;
type Datasets = "one" | "two";
type SdKind = "sample" | "population";

interface Named {
  name: string;
  report: VariationReport;
}

function warningFor(item: Named, strings: Strings): string | null {
  const { report, name } = item;
  if (report.hasNegative) {
    return t(strings.negativeWarning, { name });
  }
  if (report.cv > 1) {
    return t(strings.highWarning, { name });
  }
  return null;
}

function comparison(
  first: Named,
  second: Named,
  fmt: NumberFormatter,
  strings: Strings
): string {
  const a = first.report.cv;
  const b = second.report.cv;
  if (a === b) {
    return strings.equalLine;
  }
  const [high, low] = a > b ? [first, second] : [second, first];
  return t(strings.compareLine, {
    higher: high.name,
    high: formatPercent(fmt, high.report.cv),
    low: formatPercent(fmt, low.report.cv),
    ratio: fmt(high.report.cv / low.report.cv, { maximumFractionDigits: 1 }),
  });
}

function Results({
  items,
  fmt,
  strings,
}: {
  items: Named[];
  fmt: NumberFormatter;
  strings: Strings;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const pct = (value: number) => formatPercent(fmt, value);
  const single = items.length === 1 ? items[0] : null;
  const comparable =
    items.length === 2 && items.every((item) => item.report.cv > 0);
  return (
    <section className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <div aria-live="polite">
        <StatGrid>
          {items.map((item) => (
            <StatCard
              emphasis
              key={item.name}
              label={single ? strings.cv : t(strings.cvOf, { name: item.name })}
              value={pct(item.report.cv)}
            />
          ))}
          {single ? (
            <>
              <StatCard label={strings.mean} value={num(single.report.mean)} />
              <StatCard label={strings.sd} value={num(single.report.sd)} />
              <StatCard label={strings.count} value={fmt(single.report.n)} />
            </>
          ) : null}
        </StatGrid>
      </div>
      {single ? null : (
        <ResultTable
          head={[
            strings.colDataset,
            strings.colN,
            strings.colMean,
            strings.colSd,
            strings.colCv,
          ]}
          rows={items.map((item) => [
            item.name,
            fmt(item.report.n),
            num(item.report.mean),
            num(item.report.sd),
            pct(item.report.cv),
          ])}
        />
      )}
      {comparable ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.compareTitle}</h3>
          <Explanation>
            {comparison(items[0], items[1], fmt, strings)}
          </Explanation>
          <Explanation>{strings.compareNote}</Explanation>
        </div>
      ) : null}
      <WorkingPanel title={strings.workingTitle}>
        {items.map((item) => (
          <Formula key={item.name}>
            {t(strings.cvLine, {
              name: item.name,
              sd: num(item.report.sd),
              mean: num(item.report.mean),
              cv: pct(item.report.cv),
            })}
          </Formula>
        ))}
      </WorkingPanel>
    </section>
  );
}

export default function CoefficientOfVariationCalculatorWidget() {
  const strings = useToolUi(COEFFICIENT_OF_VARIATION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [datasets, setDatasets] = useState<Datasets>("one");
  const [sdKind, setSdKind] = useState<SdKind>("sample");
  const [textA, setTextA] = useState("");
  const [textB, setTextB] = useState("");

  const population = sdKind === "population";
  const inputs = [
    { name: strings.nameA, values: parseNumberList(textA) },
    { name: strings.nameB, values: parseNumberList(textB) },
  ].slice(0, datasets === "two" ? 2 : 1);
  const ready = inputs.every((input) => input.values.length >= 2);
  const items: Named[] = ready
    ? inputs.map((input) => ({
        name: input.name,
        report: variationReport(input.values, population) as VariationReport,
      }))
    : [];
  const zeroMean = items.find((item) => item.report.mean === 0);
  const warnings = items
    .map((item) => warningFor(item, strings))
    .filter((warning): warning is string => warning !== null);
  const count = (n: number) => (
    <>
      {fmt(n)}
      {tPlural(strings.valuesDetected, locale, n)}
    </>
  );

  return (
    <>
      <section className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <PillGroup
            label={strings.datasetsLabel}
            name="cv-datasets"
            onChange={setDatasets}
            options={[
              { value: "one", label: strings.datasetsOne },
              { value: "two", label: strings.datasetsTwo },
            ]}
            value={datasets}
          />
          <PillGroup
            label={strings.sdLabel}
            name="cv-sd"
            onChange={setSdKind}
            options={[
              { value: "sample", label: strings.sdSample },
              { value: "population", label: strings.sdPopulation },
            ]}
            value={sdKind}
          />
        </div>
        <div className={datasets === "two" ? "grid gap-4 sm:grid-cols-2" : ""}>
          <TextAreaField
            hint={count(inputs[0].values.length)}
            id="cv-a"
            label={strings.dataA}
            onChange={setTextA}
            placeholder={strings.placeholderA}
            value={textA}
          />
          {datasets === "two" ? (
            <TextAreaField
              hint={count(parseNumberList(textB).length)}
              id="cv-b"
              label={strings.dataB}
              onChange={setTextB}
              placeholder={strings.placeholderB}
              value={textB}
            />
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setTextA(strings.exampleA);
              setTextB(strings.exampleB);
            }}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              setTextA("");
              setTextB("");
            }}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </section>

      {zeroMean ? (
        <ErrorNotice>
          {t(strings.zeroMean, { name: zeroMean.name })}
        </ErrorNotice>
      ) : null}
      {warnings.map((warning) => (
        <Notice key={warning}>{warning}</Notice>
      ))}
      {items.length > 0 && !zeroMean ? (
        <Results fmt={fmt} items={items} strings={strings} />
      ) : null}
      {ready ? null : <Notice>{strings.enterPrompt}</Notice>}
    </>
  );
}
